import type { CopyPricePreviewResponse, CopyPriceRequest } from '@/api';
import { useErrorNotification } from '@/hooks/use-error-notification';
import { useNotification } from '@/hooks/use-notification';
import { Flex } from '@lego/klik-ui';
import { useRef, useState } from 'react';

import { PageWrapper } from '@/components/page-wrapper/page-wrapper';
import { CopyConfirmModal } from './components/copy-confirm-modal';
import { CopyPricePreview } from './components/copy-price-preview';
import { CopySettingsPanel } from './components/copy-settings-panel';
import type { CopySettingsSummary } from './components/copy-settings-summary-panel';
import { CopySettingsSummaryPanel } from './components/copy-settings-summary-panel';
import { useCopyPrice, useCopyPricePreview } from './copy-price-queries';

const CopyPricePage = () => {
  const showNotification = useNotification();
  const showError = useErrorNotification();

  const [previewData, setPreviewData] =
    useState<CopyPricePreviewResponse | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [lastRequest, setLastRequest] = useState<CopyPriceRequest | null>(null);
  const [summary, setSummary] = useState<CopySettingsSummary | null>(null);
  const [settingsPanelKey, setSettingsPanelKey] = useState(0);
  const confirmTriggerRef = useRef<HTMLElement | null>(null);

  const { mutate: loadPreview, isPending: isLoadingPreview } =
    useCopyPricePreview(
      (data) => setPreviewData(data),
      (err) => showError(err, 'load price preview'),
    );

  const handleBack = () => {
    setSummary(null);
    setPreviewData(null);
    setLastRequest(null);
  };

  const handleReset = () => {
    handleBack();
    // Only called after a successful "Publish prices"; remounts CopySettingsPanel with blank state.
    setSettingsPanelKey((k) => k + 1);
  };

  const { mutate: executeCopy, isPending: isExecuting } = useCopyPrice(
    (data) => {
      setIsConfirmModalOpen(false);
      showNotification({
        type: 'success',
        title: 'Prices copied',
        description: `${data.copiedCount} prices copied, ${data.skippedCount} skipped.`,
      });
      handleReset();
    },
    (err) => showError(err, 'copy prices'),
  );

  const handleLoadPreview = (
    request: CopyPriceRequest,
    summaryData: CopySettingsSummary,
  ) => {
    setPreviewData(null);
    setLastRequest(request);
    setSummary(summaryData);
    loadPreview(request);
  };

  const handleExecuteCopy = () => {
    if (lastRequest) executeCopy(lastRequest);
  };

  return (
    <PageWrapper
      layout="fill"
      contentPaddingX="0px"
      headerProps={{
        title: 'Copy prices',
      }}
      maxWidth="100%"
    >
      <Flex alignItems="flex-start" height="100%">
        {summary && (
          <CopySettingsSummaryPanel summary={summary} onBack={handleBack} />
        )}
        <Flex display={summary ? 'none' : 'flex'} height="100%">
          <CopySettingsPanel
            key={settingsPanelKey}
            isLoading={isLoadingPreview}
            onSubmit={handleLoadPreview}
          />
        </Flex>
        <CopyPricePreview
          previewData={previewData}
          isExecuting={isExecuting}
          triggerRef={confirmTriggerRef}
          onConfirmCopy={() => setIsConfirmModalOpen(true)}
        />
      </Flex>
      <CopyConfirmModal
        isOpen={isConfirmModalOpen}
        isLoading={isExecuting}
        triggerRef={confirmTriggerRef}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleExecuteCopy}
      />
    </PageWrapper>
  );
};

export default CopyPricePage;
